import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  // Map as MapIcon,
  Place as PlaceIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  DirectionsCar as CarIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

import { extractPlaceId, useGoogleMapsApi } from './googleMapsLoader';
import { resolvePlaceIdToCoords } from './placeIdResolver';

// Loading placeholder shown in place of the map preview tile while a
// place_id is being resolved to coordinates. Matches the 120px tile
// height of the actual preview so the dialog layout doesn't shift.
const LoadingMapPlaceholder = ({ label }) => (
  <Paper
    variant="outlined"
    sx={{
      height: '120px',
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 1,
      bgcolor: 'rgba(0,0,0,0.02)',
    }}
  >
    <CircularProgress size={20} />
    {label && (
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    )}
  </Paper>
);

// AdvancedMarkerElement requires a `mapId` on the parent Map — without
// one, Google gates the markers off entirely. The mapId comes from the
// useGoogleMapsApi() hook, which reads it from the
// livisibly/google_maps_api secret (MAP_ID field) via the
// google_map_api_key_fetcher Lambda. If the secret hasn't been populated
// for a given env, the hook falls back to DEMO_MAP_ID (rate-limited dev
// placeholder) — see FALLBACK_MAP_ID in googleMapsLoader.js.

// Marker content factories for AdvancedMarkerElement.
//
// AME positions content with the bottom-center anchored on the lat/lng. For
// circle markers we want the center of the circle on the position, so we
// translate the element down by half its height. This matches the visual
// behavior of the legacy SymbolPath.CIRCLE marker.
//
// Each call returns a fresh DOM node — AME requires a unique element per
// marker.
const makeCircleMarkerContent = (color) => {
  const dot = document.createElement('div');
  dot.style.cssText = `
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${color};
    border: 2px solid #FFFFFF;
    box-shadow: 0 1px 3px rgba(0,0,0,0.4);
    transform: translate(0, 50%);
  `;
  return dot;
};

const makeSvgMarkerContent = (svgPath, color) => {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    width: 36px;
    height: 36px;
    transform: translate(0, 50%);
    pointer-events: none;
  `;
  wrapper.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36"><path fill="${color}" d="${svgPath}"/></svg>`;
  return wrapper;
};

// Merchant location map component
const MerchantLocationMap = ({ merchantName, merchantGeocode }) => {
  const [showFullMap, setShowFullMap] = useState(false);
  const previewMapRef = useRef(null);
  const fullMapRef = useRef(null);
  const previewMapInstance = useRef(null);
  const fullMapInstance = useRef(null);

  const { isReady, mapId } = useGoogleMapsApi();

  // Extract the Google place_id from the stored geocode JSON.
  // useMemo'd so the dep arrays downstream (useEffect, etc.) see a
  // stable string identity across renders.
  const merchantPlaceId = React.useMemo(() => extractPlaceId(merchantGeocode), [merchantGeocode]);

  // Resolve place_id → {lat, lng} via Place.fetchFields. We do NOT
  // read the cached lat/lng from the geocode payload directly; every
  // map render pulls fresh coords from Google. See
  // resolvePlaceIdToCoords for the rationale.
  const [merchantCoords, setMerchantCoords] = useState(null);
  const [isResolvingPlace, setIsResolvingPlace] = useState(false);

  useEffect(() => {
    if (!isReady || !merchantPlaceId) {
      setMerchantCoords(null);
      setIsResolvingPlace(false);
      return undefined;
    }
    let cancelled = false;
    setIsResolvingPlace(true);
    setMerchantCoords(null);
    (async () => {
      const coords = await resolvePlaceIdToCoords(merchantPlaceId);
      if (cancelled) return;
      setMerchantCoords(coords);
      setIsResolvingPlace(false);
    })();
    return () => { cancelled = true; };
  }, [isReady, merchantPlaceId]);

  // Function to initialize the preview map
  const initializePreviewMap = () => {
    if (!merchantCoords || !previewMapRef.current || !window.google || !window.google.maps) return;

    // Clear existing map if any
    if (previewMapInstance.current) {
      // Don't re-initialize if already done
      return;
    }

    // Create map
    const map = new window.google.maps.Map(previewMapRef.current, {
      mapId,
      center: merchantCoords,
      zoom: 14,
      disableDefaultUI: true,
      clickableIcons: false
    });

    // Create marker (AdvancedMarkerElement; legacy google.maps.Marker is
    // deprecated since 2024-02-21).
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position: merchantCoords,
      map,
      title: merchantName || 'Merchant',
      content: makeCircleMarkerContent('#E53935'),
    });

    previewMapInstance.current = { map, marker };
  };

  // Function to initialize the full map
  const initializeFullMap = () => {
    if (!merchantCoords || !fullMapRef.current || !window.google || !window.google.maps) return;

    // Clear existing map if any
    if (fullMapInstance.current) {
      // Don't re-initialize if already done
      return;
    }

    // Create map
    const map = new window.google.maps.Map(fullMapRef.current, {
      mapId,
      center: merchantCoords,
      zoom: 15,
      disableDefaultUI: false
    });

    // Create marker
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position: merchantCoords,
      map,
      title: merchantName || 'Merchant',
      content: makeCircleMarkerContent('#E53935'),
      gmpClickable: true,
    });

    // Add info window
    const infoContent = `
      <div style="padding: 8px; min-width: 150px;">
        <div style="color: #666; font-size: 12px;">Merchant</div>
        <div style="font-weight: bold; color: #E53935; margin: 4px 0;">${merchantName || 'Merchant'}</div>
        <div style="font-size: 10px;">${merchantCoords.lat.toFixed(6)}, ${merchantCoords.lng.toFixed(6)}</div>
      </div>
    `;

    const infoWindow = new window.google.maps.InfoWindow({
      content: infoContent
    });

    marker.addEventListener('gmp-click', () => {
      infoWindow.open({
        anchor: marker,
        map,
      });
    });

    fullMapInstance.current = { map, marker, infoWindow };
  };

  // Initialize preview map once the Maps API is ready and we have coords.
  useEffect(() => {
    if (!isReady || !merchantCoords) return;
    initializePreviewMap();

    return () => {
      if (previewMapInstance.current) {
        if (previewMapInstance.current.map) {
          window.google && window.google.maps &&
            window.google.maps.event.clearInstanceListeners(previewMapInstance.current.map);
        }
        previewMapInstance.current = null;
      }
    };
  }, [isReady, merchantCoords]);

  // Initialize full map when dialog opens
  useEffect(() => {
    if (showFullMap && isReady && merchantCoords) {
      // Wait a bit for the dialog to fully open and the ref to be available
      setTimeout(() => {
        initializeFullMap();
      }, 100);
    }

    // Cleanup when dialog closes
    return () => {
      if (!showFullMap && fullMapInstance.current) {
        if (fullMapInstance.current.map) {
          window.google && window.google.maps &&
            window.google.maps.event.clearInstanceListeners(fullMapInstance.current.map);
        }
        fullMapInstance.current = null;
      }
    };
  }, [showFullMap, isReady, merchantCoords]);

  const handleOpenFullMap = () => {
    setShowFullMap(true);
  };

  const handleCloseFullMap = () => {
    setShowFullMap(false);
  };

  // No place_id stored — geocode is missing, malformed, or AWS-shape
  // (the brief AWS Location experiment's records won't resolve via
  // Google's Place class). Render nothing.
  if (!merchantPlaceId) {
    return null;
  }

  // place_id resolution in flight — show a placeholder so the layout
  // doesn't shift when the map appears.
  if (isResolvingPlace && !merchantCoords) {
    return <LoadingMapPlaceholder label="Loading map…" />;
  }

  // Resolution finished but coords came back null (Place.fetchFields
  // rejected — invalid/expired place_id). Render nothing rather than
  // an empty tile.
  if (!merchantCoords) {
    return null;
  }

  // Preview map (small, just shows the merchant location)
  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          height: '120px',
          position: 'relative',
          cursor: 'pointer',
          overflow: 'hidden',
          mb: 2
        }}
        onClick={handleOpenFullMap}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div
            ref={previewMapRef}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>

        {/* Overlay with map legend */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: '#E53935' }} />
            <Typography variant="caption" fontWeight="bold">
              Merchant Location
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleOpenFullMap}
            aria-label="Expand map"
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Full Map Dialog */}
      <Dialog
        open={showFullMap}
        onClose={handleCloseFullMap}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Merchant Location Map
            </Typography>
            <IconButton edge="end" onClick={handleCloseFullMap} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: '500px', width: '100%' }}>
            <div
              ref={fullMapRef}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>

          {/* Map Legend */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Chip
              icon={<BusinessIcon style={{ color: '#E53935' }} />}
              label={merchantName || "Merchant"}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFullMap}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


// Function to get the appropriate icon based on subcategory.
//
// Returns:
//   - icon: React element (MUI icon) for use in legend chips and overlay text
//   - color: hex string for use in chips, polylines, and the marker fill
//   - makeMarkerContent(): function returning a fresh DOM node for an
//     AdvancedMarkerElement. Call once per marker — AME requires unique nodes.
const getIconForSubcategory = (subcategory) => {
  let svgPath = '';
  let color = '';
  let reactIcon = null;

  switch (subcategory) {
    case 'Air Travel':
      svgPath = "M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z";
      color = '#1E88E5';
      reactIcon = <FlightIcon />;
      break;
    case 'Hotel/Lodging':
      svgPath = 'M 2,12 L 22,12 L 22,20 L 2,20 L 2,12 M 4,4 L 20,4 L 20,10 L 4,10 L 4,4';
      color = '#E91E63';
      reactIcon = <HotelIcon />;
      break;
    case 'Rental Car':
      svgPath = "M18.92,6.01C18.72,5.42 18.16,5 17.5,5h-11c-0.66,0-1.21,0.42-1.42,1.01L3,12v8c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-1h12v1c0,0.55 0.45,1 1,1h1c0.55,0 1,-0.45 1,-1v-8l-2.08,-5.99zM6.5,16c-0.83,0 -1.5,-0.67 -1.5,-1.5S5.67,13 6.5,13s1.5,0.67 1.5,1.5S7.33,16 6.5,16zM17.5,16c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5 1.5,0.67 1.5,1.5 -0.67,1.5 -1.5,1.5zM5,11l1.5,-4.5h11L19,11L5,11z";
      color = '#FF9800';
      reactIcon = <CarIcon />;
      break;
    default:
      // Default place marker
      svgPath = "M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z";
      color = '#43A047';
      reactIcon = <PlaceIcon />;
      break;
  }

  return {
    icon: reactIcon,
    color,
    makeMarkerContent: () =>
      svgPath
        ? makeSvgMarkerContent(svgPath, color)
        : makeCircleMarkerContent(color),
  };
};

// Trip map component (showing origin and destination with curved arrow)
const TripRouteMap = ({
  tripOrigin,
  tripOriginGeocode,
  tripDestination,
  tripDestinationGeocode,
  subcategory
}) => {
  const [showFullMap, setShowFullMap] = useState(false);
  const previewMapRef = useRef(null);
  const fullMapRef = useRef(null);
  const previewMapInstance = useRef(null);
  const fullMapInstance = useRef(null);

  const { isReady, mapId } = useGoogleMapsApi();

  // Get the icon based on subcategory
  const originIcon = getIconForSubcategory(subcategory);

  // Extract place_ids from the stored geocode strings. useMemo'd so the
  // dep arrays downstream see stable string identities across renders.
  const originPlaceId = React.useMemo(() => extractPlaceId(tripOriginGeocode), [tripOriginGeocode]);
  const destinationPlaceId = React.useMemo(() => extractPlaceId(tripDestinationGeocode), [tripDestinationGeocode]);

  // Resolve both place_ids → {lat, lng} via Place.fetchFields. We do
  // NOT read cached lat/lng from the geocode payload — every map render
  // pulls fresh coords from Google. See resolvePlaceIdToCoords for
  // rationale.
  const [rawOriginCoords, setRawOriginCoords] = useState(null);
  const [rawDestinationCoords, setRawDestinationCoords] = useState(null);
  const [isResolvingPlace, setIsResolvingPlace] = useState(false);

  useEffect(() => {
    if (!isReady || (!originPlaceId && !destinationPlaceId)) {
      setRawOriginCoords(null);
      setRawDestinationCoords(null);
      setIsResolvingPlace(false);
      return undefined;
    }
    let cancelled = false;
    setIsResolvingPlace(true);
    setRawOriginCoords(null);
    setRawDestinationCoords(null);
    (async () => {
      const [origin, dest] = await Promise.all([
        resolvePlaceIdToCoords(originPlaceId),
        resolvePlaceIdToCoords(destinationPlaceId),
      ]);
      if (cancelled) return;
      setRawOriginCoords(origin);
      setRawDestinationCoords(dest);
      setIsResolvingPlace(false);
    })();
    return () => { cancelled = true; };
  }, [isReady, originPlaceId, destinationPlaceId]);

  const sameCoordinates = rawOriginCoords && rawDestinationCoords &&
    rawOriginCoords.lat === rawDestinationCoords.lat &&
    rawOriginCoords.lng === rawDestinationCoords.lng;

  // If both coordinates are the same, collapse destination so we render a
  // single-point map instead of a degenerate two-point one. Stable identity
  // preserved via the source state above.
  const originCoords = rawOriginCoords;
  const destinationCoords = sameCoordinates ? null : rawDestinationCoords;

  // Check if we have both origin and destination coordinates
  const hasBothCoordinates = !!originCoords && !!destinationCoords;
  const hasAnyCoordinates = !!originCoords || !!destinationCoords;
  const hasAnyPlaceId = !!originPlaceId || !!destinationPlaceId;

  // Determine center point and zoom level for the map
  const mapSettings = React.useMemo(() => {
    // If we don't have any coordinates, return default values
    if (!hasAnyCoordinates) {
      return { center: { lat: 0, lng: 0 }, zoom: 2 };
    }

    // If we only have one point, center on it with closer zoom
    if ((originCoords && !destinationCoords) || (!originCoords && destinationCoords)) {
      return {
        center: originCoords || destinationCoords,
        zoom: 14
      };
    }

    // If we have both points, calculate the center point
    const sumLat = originCoords.lat + destinationCoords.lat;
    const sumLng = originCoords.lng + destinationCoords.lng;

    // Calculate distance to determine appropriate zoom level
    const distance = Math.sqrt(
      Math.pow(originCoords.lat - destinationCoords.lat, 2) +
      Math.pow(originCoords.lng - destinationCoords.lng, 2)
    );

    // Adjust zoom based on distance
    let zoom = 12;
    if (distance > 0.1) zoom = 10;
    if (distance > 0.5) zoom = 8;
    if (distance > 1) zoom = 6;
    if (distance > 3) zoom = 4;

    return {
      center: {
        lat: sumLat / 2,
        lng: sumLng / 2
      },
      zoom: zoom
    };
  }, [originCoords, destinationCoords, hasAnyCoordinates]);

  // Function to initialize the preview map
  const initializePreviewMap = () => {
    if (!hasAnyCoordinates || !previewMapRef.current || !window.google || !window.google.maps) return;

    // Clear existing map if any
    if (previewMapInstance.current) {
      // Don't re-initialize if already done
      return;
    }

    // Create map
    const map = new window.google.maps.Map(previewMapRef.current, {
      mapId,
      center: mapSettings.center,
      zoom: mapSettings.zoom,
      disableDefaultUI: true,
      clickableIcons: false
    });

    const markers = [];

    // Add origin marker if available
    if (originCoords) {
      const originMarker = new window.google.maps.marker.AdvancedMarkerElement({
        position: originCoords,
        map,
        title: tripOrigin || 'Origin',
        content: originIcon.makeMarkerContent(),
      });
      markers.push(originMarker);
    }

    // Add destination marker if available
    if (destinationCoords) {
      const destMarker = new window.google.maps.marker.AdvancedMarkerElement({
        position: destinationCoords,
        map,
        title: tripDestination || 'Destination',
        content: makeCircleMarkerContent('#43A047'),
      });
      markers.push(destMarker);
    }

    // Draw curved line if we have both points
    let curve = null;
    if (hasBothCoordinates) {
      // Calculate curved path between points
      const numPoints = 50;
      const points = [];

      // Calculate midpoint and offset for curve
      const midPoint = {
        lat: (originCoords.lat + destinationCoords.lat) / 2,
        lng: (originCoords.lng + destinationCoords.lng) / 2
      };

      // Calculate perpendicular offset for curve
      const dx = destinationCoords.lng - originCoords.lng;
      const dy = destinationCoords.lat - originCoords.lat;

      const perpDist = Math.sqrt(dx * dx + dy * dy) * 0.2;
      const perpLng = -dy * perpDist / Math.sqrt(dx * dx + dy * dy);
      const perpLat = dx * perpDist / Math.sqrt(dx * dx + dy * dy);

      // Apply offset to midpoint
      const controlPoint = {
        lat: midPoint.lat + perpLat,
        lng: midPoint.lng + perpLng
      };

      // Generate points along quadratic bezier curve
      for (let i = 0; i <= numPoints; i++) {
        const t = i / numPoints;

        // Quadratic bezier curve formula
        const lat = Math.pow(1 - t, 2) * originCoords.lat +
                    2 * (1 - t) * t * controlPoint.lat +
                    Math.pow(t, 2) * destinationCoords.lat;

        const lng = Math.pow(1 - t, 2) * originCoords.lng +
                    2 * (1 - t) * t * controlPoint.lng +
                    Math.pow(t, 2) * destinationCoords.lng;

        points.push({ lat, lng });
      }

      // Create the polyline
      curve = new window.google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: originIcon.color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        map: map,
        icons: [{
          icon: {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 3,
            fillColor: originIcon.color,
            fillOpacity: 1,
            strokeColor: originIcon.color,
            strokeWeight: 2
          },
          offset: '100%'
        }]
      });
    }

    previewMapInstance.current = { map, markers, curve };
  };

  // Function to initialize the full map
  const initializeFullMap = () => {
    if (!hasAnyCoordinates || !fullMapRef.current || !window.google || !window.google.maps) return;

    // Clear existing map if any
    if (fullMapInstance.current) {
      // Don't re-initialize if already done
      return;
    }

    // Create map
    const map = new window.google.maps.Map(fullMapRef.current, {
      mapId,
      center: mapSettings.center,
      zoom: mapSettings.zoom,
      disableDefaultUI: false
    });

    const markers = [];
    const infoWindows = [];

    // Add origin marker if available
    if (originCoords) {
      const originMarker = new window.google.maps.marker.AdvancedMarkerElement({
        position: originCoords,
        map,
        title: tripOrigin || 'Origin',
        content: originIcon.makeMarkerContent(),
        gmpClickable: true,
      });

      // Add info window
      const infoContent = `
        <div style="padding: 8px; min-width: 150px;">
          <div style="color: #666; font-size: 12px;">Origin</div>
          <div style="font-weight: bold; color: ${originIcon.color}; margin: 4px 0;">${tripOrigin || 'Origin'}</div>
          <div style="font-size: 10px;">${originCoords.lat.toFixed(6)}, ${originCoords.lng.toFixed(6)}</div>
          <div style="font-size: 12px; margin-top: 4px;">${subcategory || 'Location'}</div>
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoContent
      });

      originMarker.addEventListener('gmp-click', () => {
        infoWindow.open({
          anchor: originMarker,
          map,
        });
      });

      markers.push(originMarker);
      infoWindows.push(infoWindow);
    }

    // Add destination marker if available
    if (destinationCoords) {
      const destMarker = new window.google.maps.marker.AdvancedMarkerElement({
        position: destinationCoords,
        map,
        title: tripDestination || 'Destination',
        content: makeCircleMarkerContent('#43A047'),
        gmpClickable: true,
      });

      // Add info window
      const infoContent = `
        <div style="padding: 8px; min-width: 150px;">
          <div style="color: #666; font-size: 12px;">Destination</div>
          <div style="font-weight: bold; color: #43A047; margin: 4px 0;">${tripDestination || 'Destination'}</div>
          <div style="font-size: 10px;">${destinationCoords.lat.toFixed(6)}, ${destinationCoords.lng.toFixed(6)}</div>
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoContent
      });

      destMarker.addEventListener('gmp-click', () => {
        infoWindow.open({
          anchor: destMarker,
          map,
        });
      });

      markers.push(destMarker);
      infoWindows.push(infoWindow);
    }

    // Draw curved line if we have both points
    let curve = null;
    if (hasBothCoordinates) {
      // Calculate curved path between points
      const numPoints = 50;
      const points = [];

      // Calculate midpoint and offset for curve
      const midPoint = {
        lat: (originCoords.lat + destinationCoords.lat) / 2,
        lng: (originCoords.lng + destinationCoords.lng) / 2
      };

      // Calculate perpendicular offset for curve
      const dx = destinationCoords.lng - originCoords.lng;
      const dy = destinationCoords.lat - originCoords.lat;

      const perpDist = Math.sqrt(dx * dx + dy * dy) * 0.2;
      const perpLng = -dy * perpDist / Math.sqrt(dx * dx + dy * dy);
      const perpLat = dx * perpDist / Math.sqrt(dx * dx + dy * dy);

      // Apply offset to midpoint
      const controlPoint = {
        lat: midPoint.lat + perpLat,
        lng: midPoint.lng + perpLng
      };

      // Generate points along quadratic bezier curve
      for (let i = 0; i <= numPoints; i++) {
        const t = i / numPoints;

        // Quadratic bezier curve formula
        const lat = Math.pow(1 - t, 2) * originCoords.lat +
                    2 * (1 - t) * t * controlPoint.lat +
                    Math.pow(t, 2) * destinationCoords.lat;

        const lng = Math.pow(1 - t, 2) * originCoords.lng +
                    2 * (1 - t) * t * controlPoint.lng +
                    Math.pow(t, 2) * destinationCoords.lng;

        points.push({ lat, lng });
      }

      // Create the polyline
      curve = new window.google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: originIcon.color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        map: map,
        icons: [{
          icon: {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 3,
            fillColor: originIcon.color,
            fillOpacity: 1,
            strokeColor: originIcon.color,
            strokeWeight: 2
          },
          offset: '100%'
        }]
      });
    }

    fullMapInstance.current = { map, markers, infoWindows, curve };
  };

  // Initialize preview map once the Maps API is ready and we have coords.
  useEffect(() => {
    if (!isReady || !hasAnyCoordinates) return;
    initializePreviewMap();

    return () => {
      if (previewMapInstance.current) {
        if (previewMapInstance.current.map) {
          window.google && window.google.maps &&
            window.google.maps.event.clearInstanceListeners(previewMapInstance.current.map);
        }
        previewMapInstance.current = null;
      }
    };
  }, [isReady, hasAnyCoordinates, subcategory]);

  // Initialize full map when dialog opens
  useEffect(() => {
    if (showFullMap && isReady && hasAnyCoordinates) {
      // Wait a bit for the dialog to fully open and the ref to be available
      setTimeout(() => {
        initializeFullMap();
      }, 100);
    }

    // Cleanup when dialog closes
    return () => {
      if (!showFullMap && fullMapInstance.current) {
        if (fullMapInstance.current.map) {
          window.google && window.google.maps &&
            window.google.maps.event.clearInstanceListeners(fullMapInstance.current.map);
        }
        fullMapInstance.current = null;
      }
    };
  }, [showFullMap, isReady, hasAnyCoordinates, subcategory]);

  const handleOpenFullMap = () => {
    setShowFullMap(true);
  };

  const handleCloseFullMap = () => {
    setShowFullMap(false);
  };

  // No place_id stored on either end — geocode missing, malformed, or
  // AWS-shape (the brief AWS Location experiment's records won't
  // resolve via Google's Place class). Render nothing.
  if (!hasAnyPlaceId) {
    return null;
  }

  // place_id resolution in flight — show a placeholder so the layout
  // doesn't shift when the map appears.
  if (isResolvingPlace && !hasAnyCoordinates) {
    return <LoadingMapPlaceholder label="Loading map…" />;
  }

  // Resolution finished but both ends came back null. Render nothing
  // rather than an empty tile.
  if (!hasAnyCoordinates) {
    return null;
  }

  // Preview Map (small, shows origin and destination)
  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          height: '120px',
          position: 'relative',
          cursor: 'pointer',
          overflow: 'hidden',
          mb: 2
        }}
        onClick={handleOpenFullMap}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div
            ref={previewMapRef}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>

        {/* Overlay with map legend */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasBothCoordinates ? (
              // If we have both origin and destination, show the route indicators
              <>
                {React.cloneElement(originIcon.icon, { sx: { mr: 1, color: originIcon.color } })}
                <ArrowForwardIcon sx={{ fontSize: 12, mx: 0.5 }} />
                <PlaceIcon sx={{ color: '#43A047' }} />
                <Typography variant="caption" fontWeight="bold" sx={{ ml: 1 }}>
                  {subcategory || 'Trip Route'}
                </Typography>
              </>
            ) : originCoords ? (
              // If we only have origin, just show that
              <>
                {React.cloneElement(originIcon.icon, { sx: { mr: 1, color: originIcon.color } })}
                <Typography variant="caption" fontWeight="bold" sx={{ ml: 1 }}>
                  Origin: {tripOrigin || "Origin"}
                </Typography>
              </>
            ) : (
              // Otherwise we only have destination
              <>
                <PlaceIcon sx={{ mr: 1, color: '#43A047' }} />
                <Typography variant="caption" fontWeight="bold" sx={{ ml: 1 }}>
                  Destination: {tripDestination || "Destination"}
                </Typography>
              </>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={handleOpenFullMap}
            aria-label="Expand map"
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Full Map Dialog */}
      <Dialog
        open={showFullMap}
        onClose={handleCloseFullMap}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
            Trip Route Map {subcategory ? `- ${subcategory}` : ''}
            </Typography>
            <IconButton edge="end" onClick={handleCloseFullMap} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: '500px', width: '100%' }}>
            <div
              ref={fullMapRef}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>

          {/* Map Legend */}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {hasBothCoordinates ? (
              // If we have both origin and destination, show both chips
              <>
                <Chip
                  icon={React.cloneElement(originIcon.icon, { style: { color: originIcon.color } })}
                  label={tripOrigin || "Origin"}
                  variant="outlined"
                />
                <Chip
                  icon={<PlaceIcon style={{ color: '#43A047' }} />}
                  label={tripDestination || "Destination"}
                  variant="outlined"
                />
                {subcategory && (
                  <Chip
                    label={subcategory}
                    variant="outlined"
                    color="primary"
                  />
                )}
              </>
            ) : originCoords ? (
              // If we only have origin, just show origin chip
              <>
              <Chip
                icon={React.cloneElement(originIcon.icon, { style: { color: originIcon.color } })}
                label={tripOrigin || "Origin"}
                variant="outlined"
              />
              {subcategory && (
                <Chip
                  label={subcategory}
                  variant="outlined"
                  color="primary"
                />
              )}
            </>
            ) : (
              // Otherwise we only have destination chip
              <Chip
                icon={<PlaceIcon style={{ color: '#43A047' }} />}
                label={tripDestination || "Destination"}
                variant="outlined"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFullMap}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { MerchantLocationMap, TripRouteMap };
